// Email/password sign-in. OAuth providers (Google/GitHub) are deferred —
// add them back via supabase.auth.signInWithOAuth({ provider }) and a
// matching button when ready.
//
// New users today: create them in Supabase dashboard → Authentication →
// Users → Add user. Then sign in here. A self-serve sign-up form can be
// added later if/when this opens up to a wider audience.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";

export function SignInPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function withPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        setError(error.message);
        return;
      }
      navigate("/");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-6 rounded-lg border bg-card p-8 shadow-sm">
        <div className="space-y-2 text-center">
          <div className="text-3xl" aria-hidden>
            🧌
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Sign in to Job Goblin
          </h1>
          <p className="text-sm text-muted-foreground">
            Approval required. <Badge variant="accent">invite-only</Badge>
          </p>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form className="space-y-4" onSubmit={withPassword}>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            <Mail className="mr-2 h-4 w-4" />
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          New here?{" "}
          <Link to="/sign-up" className="text-info underline-offset-4 hover:underline">
            Request access
          </Link>{" "}
          — your account will be queued for approval.
        </p>
      </div>
    </div>
  );
}
